import { ErrorState, RootState } from '../../redux/AppReducer';
import { BrandVM, sortOptionType } from '../../common/view-model';
import { Events, Product } from '../../common/model';
import './BrandProfileComponent.scss';
import { FC, useCallback, useEffect, useState } from 'react';
import { aboutBrandState } from './model';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { GET_PRODUCTS } from '../../graphql/queries/products';
import { AppActions } from '../../redux/AppActions';
import { GoBackButtonComponent } from '../UI/GoBackButtonComponent/GoBackButtonComponent';
import { useLazyQuery } from '@apollo/client';
import { GENERAL_ERRORS, serverError } from '../../common/constants';
import { ContactInformationComponent } from '../product-detail/accordion/contactInformation/ContactInformationComponent';
import BrandHeaderComponent from './brand-header-component/BrandHeaderComponent';
import BrandProductsComponent from './brand-products-component/BrandProductsComponent';
import { useAppDispatch } from '../../redux/store';
import { AuthActions } from '../../redux/AuthActions';

interface IBrandProfileComponentProps {
	brand: BrandVM;
	currentEvent?: Events | null;
}
const BrandProfileComponent: FC<IBrandProfileComponentProps> = ({ brand, currentEvent }) => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [state] = useState({ ...aboutBrandState });
	const { ingredientsTemplateId } = useSelector((state: RootState) => state.appState?.userState!);
	const { sortBySelected, sortByOptions } = useSelector(
		(state: RootState) => state.appState?.filterState!
	);
	const tokenHasError = useSelector(
		({ appState: state }: RootState) => state.authState?.tokenHasError
	);
	const { isLoadingData } = useSelector((state: RootState) => state.appState);
	const isLoadingCallback = useCallback(
		(loading: boolean) => dispatch(AppActions.setIsLoadingData(loading)),
		[dispatch]
	);
	const [products, setProducts] = useState<Product[]>([]);
	const sortBy =
		sortBySelected?.sortType === sortOptionType.MOST_RELEVANT
			? sortByOptions?.find(x => x.sortType === sortOptionType.ALPHABETICAL)
			: sortBySelected;

	const [getProducts, { loading, data, error }] = useLazyQuery(GET_PRODUCTS);
	const handleError = (error: ErrorState) => dispatch(AppActions.setGeneralError(error));
	const handleUnauthorized = () => dispatch(AppActions.unauthorizedError());
	useEffect(() => {
		if (tokenHasError === 0 && currentEvent !== undefined) {
			getProducts({
				variables: {
					genericFilters: [
						{
							key: 'Brand',
							ids: [brand.id]
						}
					],
					sortAttribute: sortBy?.productSort.attribute,
					sortDirection: sortBy?.productSort.order,
					templateId: String(ingredientsTemplateId),
					expoEventId: currentEvent?.id
				}
			});
		}
	}, []);

	useEffect(() => {
		isLoadingCallback(true);
		if (!error && !loading && data?.products?.products) {
			setProducts(data?.products?.products);
			isLoadingCallback(false);
		}
	}, [loading, data]);

	useEffect(() => {
		if (error?.message === 'Unauthorized') {
			if (tokenHasError === 0) {
				dispatch(AuthActions.setTokenError(1));
			}
		}
		if (error?.message === GENERAL_ERRORS.USER_TOKEN_EXPIRED) {
			handleUnauthorized();
		}
		if (error) {
			handleError(serverError);
		}
	}, [error]);

	const handleNavigateBack = useCallback(() => {
		navigate(-1);
	}, [navigate]);

	return (
		<div data-testid='brand-profile-container' className='brand-profile-container'>
			<GoBackButtonComponent
				handelNavigate={handleNavigateBack}
				text={state.backToPageText}
				icon={<i className='fa fa-arrow-left' aria-hidden='true'></i>}
			/>
			<BrandHeaderComponent brand={brand} showShareMenu={true}></BrandHeaderComponent>
			{!isLoadingData && products.length > 0 ? (
				<BrandProductsComponent products={products} brand={brand}></BrandProductsComponent>
			) : null}
			<div className={`contact-container ${products.length <= 2 ? 'short-products-wrapper' : ''}`}>
				<div className='brand-contact-title-container'>
					<span className='brand-contact-title'>{state.contactBrandTitle}</span>
				</div>
				<ContactInformationComponent
					brokers={brand?.brokers}
					contact={{
						name: brand.contact_name,
						email: brand.contact_email,
						phone: brand.contact_phone
					}}
					productRep={brand?.prod_rep}
				/>
			</div>
		</div>
	);
};

export default BrandProfileComponent;
